import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

// Toast is the in-place notification primitive for transient feedback that
// must NOT depend on scroll position. Manifest §1: this is local UI state, so
// it lives in useState + Context — never an atom (a global-lifetime atom would
// leak toasts across page visits).
//
// Why this exists: an error surfaced at the top of a long page (e.g. a toggle
// failure at the bottom of the repo list) is invisible until the user scrolls.
// A fixed-position toast is seen wherever the user already is.
export type ToastKind = "error" | "success" | "info";

export type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
};

// The API is intentionally tiny: one method per kind. Callers should not be
// able to construct arbitrary toast shapes — the kind drives the color token,
// nothing else.
export type ToastApi = {
  error: (message: string) => void;
  success: (message: string) => void;
  info: (message: string) => void;
};

// Internal context: the live toast list (read by Toaster) plus a dismiss fn.
// Split from ToastApi so feature code only ever sees the emit API, not the
// render plumbing.
type ToastInternal = {
  toasts: Toast[];
  dismiss: (id: number) => void;
};

const ToastApiContext = createContext<ToastApi | null>(null);
const ToastInternalContext = createContext<ToastInternal | null>(null);

// Auto-dismiss after this many ms. Long enough to read a short error, short
// enough not to pile up. Manual close is always available too.
const AUTO_DISMISS_MS = 5000;

type Props = {
  children: ReactNode;
};

export function ToastProvider({ children }: Props) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Monotonic id source. A ref (not state) so emitting doesn't itself cause a
  // render and the id stays unique across the component's life.
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      // Auto-dismiss timer. dismiss() is idempotent (filter by id), so a manual
      // close before the timer fires is safe — the timer just no-ops.
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  // Stable emit API so consumers don't re-render when the toast list changes
  // (manifest §1: keep the public surface stable).
  const api = useMemo<ToastApi>(
    () => ({
      error: (message) => push("error", message),
      success: (message) => push("success", message),
      info: (message) => push("info", message),
    }),
    [push],
  );

  const internal = useMemo<ToastInternal>(() => ({ toasts, dismiss }), [toasts, dismiss]);

  return (
    <ToastApiContext.Provider value={api}>
      <ToastInternalContext.Provider value={internal}>{children}</ToastInternalContext.Provider>
    </ToastApiContext.Provider>
  );
}

// useToast is the consumer hook for feature code: `toast.error(msg)`.
export function useToast(): ToastApi {
  const ctx = useContext(ToastApiContext);
  if (ctx == null) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}

// useToastList is for the Toaster renderer only. Kept separate so feature code
// can't accidentally read/mutate the raw list.
export function useToastList(): ToastInternal {
  const ctx = useContext(ToastInternalContext);
  if (ctx == null) {
    throw new Error("useToastList must be used within a ToastProvider");
  }
  return ctx;
}
